import {
  BankEntryFormDialog,
  type BankEntryFormInitial,
  type BankEntryFormValues,
} from '@/components/bank-entries/BankEntryFormDialog';
import {
  useAddFundTransactionBankEntry,
  useUpdateFundTransactionBankEntry,
} from '@/hooks/react-query/useFundTransactions';
import type { BankEntryResponse } from '@/types/fund-transactions';

export type BankDialogState =
  { mode: 'add' } | { mode: 'edit'; entry: BankEntryResponse } | null;

interface FundBankEntryDialogProps {
  state: BankDialogState;
  onClose: () => void;
  fundId: string;
  transactionId: string;
  transactionAmount: number;
  transactionCurrency: string;
  transactionCurrencyId: string;
  transactionDate: string;
}

function initialFormValues(
  state: BankDialogState,
  transactionDate: string,
  transactionAmount: number
): BankEntryFormInitial | undefined {
  if (state?.mode === 'edit') {
    return {
      valueDate: state.entry.value_date,
      amount: state.entry.amount,
      iban: state.entry.counterparty_iban,
      reference: state.entry.payment_reference ?? '',
    };
  }
  if (state?.mode === 'add') {
    return { valueDate: transactionDate, amount: transactionAmount };
  }
  return undefined;
}

export function FundBankEntryDialog({
  state,
  onClose,
  fundId,
  transactionId,
  transactionAmount,
  transactionCurrency,
  transactionCurrencyId,
  transactionDate,
}: FundBankEntryDialogProps) {
  const addEntry = useAddFundTransactionBankEntry(fundId, transactionId);
  const updateEntry = useUpdateFundTransactionBankEntry(fundId, transactionId);
  const submitting = addEntry.isPending || updateEntry.isPending;
  const isEdit = state?.mode === 'edit';

  const initial = initialFormValues(state, transactionDate, transactionAmount);

  const handleSubmit = async (values: BankEntryFormValues) => {
    const payload = {
      value_date: values.valueDate,
      amount: String(values.amount),
      currency_id: transactionCurrencyId,
      counterparty_iban: values.iban,
      payment_reference: values.reference || null,
    };
    if (state?.mode === 'edit') {
      await updateEntry.mutateAsync({
        entryId: state.entry.id,
        data: payload,
      });
    } else {
      await addEntry.mutateAsync({ manual_entry: payload });
    }
  };

  return (
    <BankEntryFormDialog
      open={state !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={isEdit ? 'Edit bank entry' : 'Add bank entry'}
      description={
        isEdit
          ? 'Update the manually-entered bank statement line for this transaction.'
          : 'Record the wire payment that confirms this fund transaction.'
      }
      currencyLabel={transactionCurrency}
      initial={initial}
      submitLabel={isEdit ? 'Save' : 'Add'}
      submitting={submitting}
      onSubmit={handleSubmit}
    />
  );
}
